import i18n from "i18next";

export type UpdateRow = { field: string; from?: any; to?: any };
export type CreateRow = { field: string; value: any };

export const pretty = (v: any) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
};


export const formatValue = (value: any): string => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/.test(value)) {
    const date = new Date(value);
    return date.toLocaleString();
  }

  const prettyValue = pretty(value);
  return i18n.t(prettyValue) || prettyValue;
};

export const joinPath = (base: string, key: string | number) =>
  typeof key === "number" ? `${base}[${key}]` : base ? `${base}.${key}` : key;

export const isCreatePayload = (changes?: Record<string, any>) =>
  !!changes && Object.prototype.hasOwnProperty.call(changes, "after") && typeof changes.after === "object";

/** ----- CREATE ----- */
export const flattenAfter = (obj: any, base = "", rows: CreateRow[] = []): CreateRow[] => {
  if (obj === null || obj === undefined || typeof obj !== "object") {
    rows.push({ field: base || "(root)", value: obj });
    return rows;
  }
  if (Array.isArray(obj)) {
    obj.forEach((v, i) => flattenAfter(v, joinPath(base, i), rows));
    return rows;
  }
  for (const [k, v] of Object.entries(obj)) {
    const p = joinPath(base, k);
    if (v && typeof v === "object") flattenAfter(v, p, rows);
    else rows.push({ field: p, value: v });
  }
  return rows;
};

/** ----- UPDATE ----- */
const hasFromTo = (v: any) => v && typeof v === "object" && ("from" in v || "to" in v);

const labelOf = (item: any) => {
  if (item == null) return "—";
  if (typeof item !== "object") return String(item);
  if ("label" in item && item.label != null) return String((item as any).label);
  if ("value" in item && item.value != null) return String((item as any).value);
  if ("start" in item && "end" in item) return `${item.start}-${item.end}`;
  try {
    return JSON.stringify(item);
  } catch {
    return String(item);
  }
};

const keyOf = (item: any) => {
  if (item == null) return "__null__";
  if (typeof item !== "object") return `__prim__:${String(item)}`;
  if ("value" in item && item.value != null) return `value:${String(item.value)}`;
  if ("label" in item && item.label != null) return `label:${String(item.label)}`;
  if ("id" in item && item.id != null) return `id:${String(item.id)}`;
  if ("start" in item && "end" in item) return `range:${item.start}-${item.end}`;
  try {
    return `json:${JSON.stringify(item)}`;
  } catch {
    return `raw:${String(item)}`;
  }
};

const arrayDiff = (fromArr: any[] = [], toArr: any[] = []) => {
  const fromMap = new Map<string, any>();
  const toMap = new Map<string, any>();

  fromArr.forEach((x) => fromMap.set(keyOf(x), x));
  toArr.forEach((x) => toMap.set(keyOf(x), x));

  const removed: any[] = [];
  const added: any[] = [];

  // ✅ No for...of, safe for ES5
  fromMap.forEach((v, k) => {
    if (!toMap.has(k)) {
      removed.push(v);
    }
  });

  toMap.forEach((v, k) => {
    if (!fromMap.has(k)) {
      added.push(v);
    }
  });

  return { added, removed };
};

const pushArrayDiffRows = (rows: UpdateRow[], field: string, fromArr: any[], toArr: any[]) => {
  rows.push({
    field,
    from: Array.isArray(fromArr) ? fromArr.length : undefined,
    to: Array.isArray(toArr) ? toArr.length : undefined,
  });
  const { added, removed } = arrayDiff(fromArr || [], toArr || []);
  if (added.length) rows.push({ field: `${field}.added`, from: "—", to: added.map(labelOf) });
  if (removed.length) rows.push({ field: `${field}.removed`, from: removed.map(labelOf), to: "—" });
};

export const normalizeUpdateChanges = (changes?: Record<string, any>): UpdateRow[] => {
  if (!changes) return [];
  const rows: UpdateRow[] = [];
  const walk = (node: any, base = "") => {
    if (!node || typeof node !== "object") {
      rows.push({ field: base || "(root)", from: undefined, to: node });
      return;
    }
    if (hasFromTo(node)) {
      const fromVal = (node as any).from;
      const toVal = (node as any).to;
      if (Array.isArray(fromVal) || Array.isArray(toVal)) {
        pushArrayDiffRows(rows, base || "(root)", fromVal, toVal);
        return;
      }
      rows.push({ field: base || "(root)", from: fromVal, to: toVal });
      return;
    }
    for (const [k, v] of Object.entries(node)) {
      const path = joinPath(base, k);
      walk(v, path);
    }
  };
  walk(changes);
  return rows;
};


// NEW: split create "after" into scalars vs arrays
export const splitCreateAfter = (after: any) => {
  const scalarRows: CreateRow[] = [];
  const arraySections: Array<{ key: string; items: any[] }> = [];

  if (!after || typeof after !== "object") return { scalarRows, arraySections };

  for (const [k, v] of Object.entries(after)) {
    if (Array.isArray(v)) {
      arraySections.push({ key: k, items: v });
    } else if (v && typeof v === "object") {
      // keep nested objects flattened into scalar rows (existing behavior)
      flattenAfter(v, k, scalarRows);
    } else {
      scalarRows.push({ field: k, value: v });
    }
  }
  return { scalarRows, arraySections };
};
